import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './RouteExplorer.css';
import StopMap from './StopMap';

function RouteExplorer() {
    const hostname = window.location.hostname;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('ngrok');
    const API_BASE_URL = isLocal
        ? '/api'
        : 'https://buzzbus-production.up.railway.app/api';

    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stopETAs, setStopETAs] = useState({});
    const [showClosestStop, setShowClosestStop] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [closestStopId, setClosestStopId] = useState(null);
    const [expandedStopId, setExpandedStopId] = useState(null); // Track which stop is expanded (only one at a time)

    const fetchRoutes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/RouteSearch/map-routes`);

            // Ensure we have an array
            const routesData = Array.isArray(response.data) ? response.data : [];
            console.log('[RouteExplorer] Fetched routes:', routesData.length, 'routes');
            if (routesData.length > 0) {
                console.log('[RouteExplorer] Sample route data:', routesData[0]);
                console.log('[RouteExplorer] All routes visibility:', routesData.map(r => ({
                    description: r.description,
                    IsVisibleOnMap: r.IsVisibleOnMap,
                    routeId: r.routeId
                })));
            }

            setRoutes(routesData);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching routes:', err);
            setError('Failed to load routes. Please try again.');
            setLoading(false);
        }
    }, [API_BASE_URL]);

    // Auto-select Red route on initial load
    useEffect(() => {
        if (routes.length > 0 && !selectedRoute) {
            const redRoute = routes.find(r => r.routeId === "20");
            if (redRoute) {
                setSelectedRoute(redRoute);
            }
        }
    }, [routes, selectedRoute]);

    const fetchETAs = useCallback(async (routeId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/StopTimes/route/${routeId}/stop-arrivals`);

            // Convert array to map for easy lookup
            const etaMap = {};
            response.data.stops.forEach(stop => {
                etaMap[stop.routeStopId] = stop.arrivals;
            });
            setStopETAs(etaMap);
        } catch (err) {
            console.error('Failed to load ETAs:', err);
        }
    }, [API_BASE_URL]);

    // Fetch all routes on component mount
    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch ETAs when a route is selected
    useEffect(() => {
        if (selectedRoute) {
            fetchETAs(selectedRoute.routeId);
        } else {
            setStopETAs({});
        }
    }, [selectedRoute, fetchETAs]);

    // Auto-refresh ETAs every 60 seconds
    useEffect(() => {
        if (!selectedRoute) return;

        const interval = setInterval(() => {
            fetchETAs(selectedRoute.routeId);
        }, 60000); // 60 seconds

        return () => clearInterval(interval);
    }, [selectedRoute, fetchETAs]);

    // Handle geolocation for closest stop
    const handleClosestStopToggle = () => {
        if (!showClosestStop) {
            // Request location permission
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setUserLocation({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        });
                        setShowClosestStop(true);
                    },
                    (error) => {
                        console.error('Error getting location:', error);
                        alert('Unable to get your location. Please enable location services.');
                    }
                );
            } else {
                alert('Geolocation is not supported by your browser.');
            }
        } else {
            setShowClosestStop(false);
            setUserLocation(null);
            setClosestStopId(null);
        }
    };

    // Calculate distance between two coordinates (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in km
    };

    // Find closest stop when user location and route are available
    useEffect(() => {
        if (showClosestStop && userLocation && selectedRoute && selectedRoute.stops) {
            let closestStop = null;
            let minDistance = Infinity;

            selectedRoute.stops.forEach(stop => {
                if (stop.latitude && stop.longitude) {
                    const distance = calculateDistance(
                        userLocation.lat,
                        userLocation.lng,
                        stop.latitude,
                        stop.longitude
                    );
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestStop = stop;
                    }
                }
            });

            if (closestStop) {
                setClosestStopId(closestStop.routeStopId);
            }
        } else {
            setClosestStopId(null);
        }
    }, [showClosestStop, userLocation, selectedRoute]);

    // Toggle stop expansion (only one at a time)
    const toggleStopExpansion = (stopId) => {
        setExpandedStopId(prev => prev === stopId ? null : stopId);
    };

    if (loading) {
        return (
            <div className="route-explorer-loading">
                <div className="loading-spinner"></div>
                <p>Loading routes...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="route-explorer-error">
                <p>{error}</p>
                <button onClick={fetchRoutes}>Retry</button>
            </div>
        );
    }

    return (
        <div className="route-explorer">
            <div style={{
                display: 'flex',
                minHeight: 'calc(100vh - 200px)',
                gap: '0'
            }}>
                {/* Vertical Route Sidebar */}
                <div style={{
                    width: '90px',
                    background: 'white',
                    borderRight: '3px solid #333',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    overflow: 'auto'
                }}>
                    {Array.isArray(routes) && routes
                        .sort((a, b) => {
                            // Sort active routes first using isActive from backend
                            const aActive = a.isActive !== false;
                            const bActive = b.isActive !== false;
                            if (aActive && !bActive) return -1;
                            if (!aActive && bActive) return 1;
                            return 0;
                        })
                        .map(route => {
                            const name = route.description;
                            let displayName = name;
                            if (name.includes('Weekend Atlantic')) displayName = 'Wknd Atl.';
                            else if (name.includes('Weekend Midtown')) displayName = 'Wknd Mid.';
                            else if (name.includes('Night Gold')) displayName = 'Night Gold';
                            else if (name.includes('Northside') || name.includes('Atlantic')) displayName = 'Northside';
                            else if (name.includes('NARA') || name.includes('Science')) displayName = 'NARA/Sci.';

                            const isActive = selectedRoute?.routeId === route.routeId;
                            const isRouteActive = route.isActive !== false; // Use isActive from backend

                            return (
                                <button
                                    key={route.routeId}
                                    onClick={() => setSelectedRoute(route)}
                                    style={{
                                        padding: '14px 8px',
                                        border: 'none',
                                        background: isActive ? '#fef9e7' : 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px',
                                        borderRight: isActive ? '5px solid #eab308' : '5px solid transparent',
                                        transition: 'all 0.2s',
                                        borderBottom: '1px solid #f0f0f0',
                                        outline: 'none'
                                    }}
                                >
                                    <div style={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        background: route.mapLineColor,
                                        transform: isActive ? 'scale(1.15)' : 'scale(1)',
                                        transition: 'transform 0.2s',
                                        boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                                        opacity: isRouteActive ? 1 : 0.4,
                                        filter: isRouteActive ? 'none' : 'grayscale(50%)'
                                    }}></div>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: isActive ? '700' : '600',
                                        color: isActive ? '#333' : (isRouteActive ? '#666' : '#9ca3af'),
                                        textAlign: 'center'
                                    }}>{displayName}</div>
                                </button>
                            );
                        })}
                </div>

                {/* Route Details Section */}
                <div style={{
                    flex: 1,
                    background: '#fafafa',
                    overflowY: 'auto'
                }}>

                    {/* Selected Route Details */}
                    {selectedRoute && (
                        <div className="route-details-section">
                            <div className="route-details-header">
                                <div className="route-details-title">
                                    <div
                                        className="route-color-badge"
                                        style={{ backgroundColor: selectedRoute.mapLineColor }}
                                    ></div>
                                    <h2>{selectedRoute.description}</h2>
                                </div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    userSelect: 'none'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={showClosestStop}
                                        onChange={handleClosestStopToggle}
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <span>Show Closest Stop</span>
                                </label>
                            </div>

                            <div className="stops-timeline">
                                <div className="timeline-header">

                                    {<span className="stop-total">Arrival Times</span>}
                                    {selectedRoute && selectedRoute.isActive === false && (
                                        <span style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            fontSize: '11px',
                                            fontWeight: '700',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginLeft: '12px'
                                        }}>
                                            Inactive
                                        </span>
                                    )}
                                </div>

                                <div className="stops-list">
                                    {(() => {
                                        // Sort stops by order
                                        let sortedStops = [...selectedRoute.stops].sort((a, b) => a.order - b.order);

                                        // If showing closest stop, move it to the top
                                        if (showClosestStop && closestStopId) {
                                            const closestIndex = sortedStops.findIndex(s => s.routeStopId === closestStopId);
                                            if (closestIndex > 0) {
                                                const [closestStop] = sortedStops.splice(closestIndex, 1);
                                                sortedStops.unshift(closestStop);
                                            }
                                        }

                                        return sortedStops.map((stop, index) => {
                                            const isClosest = showClosestStop && stop.routeStopId === closestStopId;

                                            return (
                                                <div
                                                    key={stop.routeStopId}
                                                    className="stop-item"
                                                    style={{
                                                        background: isClosest ? '#fef9e7' : 'transparent',
                                                        padding: isClosest ? '12px' : '0',
                                                        borderRadius: isClosest ? '8px' : '0',
                                                        marginBottom: isClosest ? '8px' : '0',
                                                        position: 'relative',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    <div className="stop-timeline-marker">
                                                        <div
                                                            className="stop-dot"
                                                            style={{ borderColor: selectedRoute.mapLineColor }}
                                                        ></div>
                                                        {index < sortedStops.length - 1 && (
                                                            <div
                                                                className="stop-line"
                                                                style={{ backgroundColor: selectedRoute.mapLineColor }}
                                                            ></div>
                                                        )}
                                                    </div>

                                                    <div className="stop-content">
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                cursor: 'pointer',
                                                                padding: '0px 0'
                                                            }}
                                                            onClick={() => toggleStopExpansion(stop.routeStopId)}
                                                        >
                                                            <h4 className="stop-name" style={{ margin: 0 }}>{stop.description}</h4>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                {isClosest && (
                                                                    <span style={{
                                                                        background: '#eab308',
                                                                        color: 'white',
                                                                        fontSize: '11px',
                                                                        fontWeight: '700',
                                                                        padding: '4px 10px',
                                                                        borderRadius: '12px',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px'
                                                                    }}>
                                                                        Closest
                                                                    </span>
                                                                )}
                                                                <span style={{
                                                                    fontSize: '18px',
                                                                    color: '#9ca3af',
                                                                    transition: 'transform 0.2s',
                                                                    transform: expandedStopId === stop.routeStopId ? 'rotate(180deg)' : 'rotate(0deg)'
                                                                }}>
                                                                    ▼
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* ETAs - Show above map */}
                                                        {stopETAs[stop.routeStopId] && stopETAs[stop.routeStopId].length > 0 && (
                                                            <div className="stop-eta" style={{ marginTop: '8px' }}>
                                                                {stopETAs[stop.routeStopId].map((arrival, idx) => (
                                                                    <span key={idx} className="eta-time">
                                                                        {arrival.minutes} min
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Embedded Map Dropdown */}
                                                        {expandedStopId === stop.routeStopId && (
                                                            <div style={{
                                                                marginTop: '12px',
                                                                borderRadius: '8px',
                                                                overflow: 'visible',
                                                                border: '1px solid #e5e7eb'
                                                            }}>
                                                                <div style={{ height: '200px', overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
                                                                    <StopMap
                                                                        stop={{
                                                                            latitude: stop.latitude,
                                                                            longitude: stop.longitude,
                                                                            name: stop.description
                                                                        }}
                                                                        routeColor={selectedRoute.mapLineColor}
                                                                    />
                                                                </div>
                                                                <a
                                                                    href={`https://www.google.com/maps/search/?api=1&query=${stop.latitude},${stop.longitude}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '100%',
                                                                        marginTop: '1rem',
                                                                        padding: '0.75rem 1rem',
                                                                        backgroundColor: '#FACC15',
                                                                        color: '#0f172a',
                                                                        textDecoration: 'none',
                                                                        fontWeight: '700',
                                                                        borderRadius: '0.375rem',
                                                                        gap: '0.5rem',
                                                                        transition: 'all 0.2s ease',
                                                                        border: 'none',
                                                                        fontSize: '0.95rem'
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.target.style.backgroundColor = '#EAB308';
                                                                        e.target.style.transform = 'translateY(-1px)';
                                                                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.target.style.backgroundColor = '#FACC15';
                                                                        e.target.style.transform = 'translateY(0)';
                                                                        e.target.style.boxShadow = 'none';
                                                                    }}
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginTop: '-2px' }}>
                                                                        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                                                                    </svg>
                                                                    Open in Google Maps
                                                                </a>
                                                            </div>
                                                        )}

                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {!selectedRoute && (
                        <div className="route-details-empty">
                            <div className="empty-state-icon"></div>
                            <h3>Select a route to view arrival times for stops.</h3>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RouteExplorer;
